import styled, { css } from 'styled-components';
import { colors, borderRadius, elevation, spacing, typography } from '@izettle/otto';
import { Divider } from '../../divider/index.js';
import { IconButton } from '../../icon-button/index.js';
import { ModalSize } from '../../../enumerations/modal-size.js';

const MODAL_PADDING_VARIABLE = "--iz-modal-padding";
const setPaddingVariable = _ref => {
  let {
    isMobile
  } = _ref;
  return `
  ${MODAL_PADDING_VARIABLE}: ${isMobile ? spacing.small : spacing.medium};
`;
};
const ModalBaseOverflowingContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__ModalBaseOverflowingContainer",
  componentId: "zettle__sc-8zc0lr-0"
})(["overflow-y:scroll;display:flex;position:absolute;align-items:center;justify-content:center;width:calc(100% - var(", ") * 2);padding:var(", ") 0;top:0;min-height:calc(100% - var(", ") * 2);height:fit-content;"], MODAL_PADDING_VARIABLE, MODAL_PADDING_VARIABLE, MODAL_PADDING_VARIABLE);
const ModalBaseBackdrop = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__ModalBaseBackdrop",
  componentId: "zettle__sc-8zc0lr-1"
})(["", " top:0;bottom:0;left:0;right:0;position:fixed;display:flex;justify-content:center;align-items:center;flex-direction:column;background:rgba(0,0,0,0.2);z-index:9000;padding:var(", ");&.transition-enter{opacity:0;transform:scale(1.01);}&.transition-enter-active{opacity:1;transform:scale(1);transition:opacity 0.2s,transform 0.2s;}&.transition-exit{opacity:1;transform:scale(1);}&.transition-exit-active{opacity:0;transform:scale(1.01);transition:opacity 0.2s,transform 0.2s;}", ""], setPaddingVariable, MODAL_PADDING_VARIABLE, _ref2 => {
  let {
    isOverflowing
  } = _ref2;
  return isOverflowing && `
    overflow-y: scroll;
  `;
});
const mixinContainerSize = _ref3 => {
  let {
    size,
    isMobile
  } = _ref3;
  if (isMobile) {
    return css`
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100%;
      width: 100%;
      box-shadow: none;
      border-radius: 0;
      padding-bottom: ${spacing.small};
    `;
  }
  switch (size) {
    case ModalSize.Small:
      return css`
        width: 400px;
        min-height: 0;
        max-height: 400px;
      `;
    case ModalSize.Large:
      return css`
        width: 800px;
        min-height: 0;
        max-height: 600px;
      `;
    case ModalSize.Medium:
    default:
      return css`
        width: 600px;
        min-height: 0;
        max-height: 600px;
      `;
  }
};
const ModalBaseContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__ModalBaseContainer",
  componentId: "zettle__sc-8zc0lr-2"
})(["background:", ";border-radius:", ";display:flex;box-shadow:", ";flex-direction:column;max-width:100%;position:relative;overflow:hidden;padding-bottom:", ";", ";", ""], colors.background.default, borderRadius.large, elevation.level4, _ref4 => {
  let {
    hasFooter
  } = _ref4;
  return hasFooter ? spacing.medium : `calc(${spacing.medium} + ${spacing.small})`;
}, mixinContainerSize, _ref5 => {
  let {
    isOverflowing
  } = _ref5;
  return isOverflowing && `
    max-height: initial;
  `;
});
const ModalBaseContent = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__ModalBaseContent",
  componentId: "zettle__sc-8zc0lr-3"
})(["min-height:0;overflow-y:auto;flex:1;display:flex;flex-direction:column;padding:0 var(", ");", " ", ""], MODAL_PADDING_VARIABLE, _ref6 => {
  let {
    hideTextLabel
  } = _ref6;
  return hideTextLabel ? `padding-top: calc(${spacing.large} + ${spacing.xxSmall});` : "";
}, _ref7 => {
  let {
    preventHorizontalOverflow
  } = _ref7;
  return preventHorizontalOverflow ? `overflow-x: hidden;` : "";
});
const ModalBaseHeader = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__ModalBaseHeader",
  componentId: "zettle__sc-8zc0lr-4"
})(["min-height:fit-content;padding-right:calc(", " * 2);", ";"], spacing.large, _ref8 => {
  let {
    hideTextLabel
  } = _ref8;
  return hideTextLabel ? css`
          visibility: hidden;
          position: absolute;
        ` : css`
          visibility: visible;
        `;
});
const ModalBaseHeaderRow = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__ModalBaseHeaderRow",
  componentId: "zettle__sc-8zc0lr-5"
})(["width:100%;padding:", ";padding:var(", ");"], spacing.medium, MODAL_PADDING_VARIABLE);
const ModalBaseLabel = /*#__PURE__*/styled.p.withConfig({
  displayName: "styles__ModalBaseLabel",
  componentId: "zettle__sc-8zc0lr-6"
})(["margin:0 ", " 0 0;align-items:center;display:flex;", ""], spacing.large, typography.regularBold);
const getIconPosition = _ref9 => {
  let {
    isMobile
  } = _ref9;
  return isMobile ? spacing.small : `calc(${spacing.small} + ${spacing.tiny})`;
};
const StyledIconButton = /*#__PURE__*/styled(IconButton).withConfig({
  displayName: "styles__StyledIconButton",
  componentId: "zettle__sc-8zc0lr-7"
})(["position:absolute;top:", ";right:", ";"], getIconPosition, getIconPosition);
const ModalBaseEndFocus = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__ModalBaseEndFocus",
  componentId: "zettle__sc-8zc0lr-8"
})(["height:0;"]);
const StyledDividerHeader = /*#__PURE__*/styled(Divider).withConfig({
  displayName: "styles__StyledDividerHeader",
  componentId: "zettle__sc-8zc0lr-9"
})(["margin:0;"]);

export { ModalBaseBackdrop, ModalBaseContainer, ModalBaseContent, ModalBaseEndFocus, ModalBaseHeader, ModalBaseHeaderRow, ModalBaseLabel, ModalBaseOverflowingContainer, StyledDividerHeader, StyledIconButton };
//# sourceMappingURL=styles.js.map
